// src/utils/triageUtils.js
//
// Shared action plan utilities — pure functions, no React imports.
// Used by:
//   useAutoTriage.js      → build + serialise action plan before POST (Step 5)
//   TriageReportModal.js  → render Your Action Items tab (Tab 06, formerly "Action Plan")
//   buildPrintHtml()      → PDF export action plan section
//
// CLINICAL: Priority classification follows Previu Clinical Architecture v5.7
// Intervention Prioritization Engine (Section A2c).
// Max 3 simultaneous P1 interventions (high-friction) per v5.7 rule.
//
// Action item shape — matches existing TriageReportModal ActionPlanTab:
//   { id, priority, category, title, body, window, tags }

// ─────────────────────────────────────────────────────────────────────────────
// buildActionPlan
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Builds the structured action plan from a triage result.
 * Mirrors the existing inline logic in ActionPlanTab so behaviour is identical.
 *
 * @param {object} triage   - triageResult object (TriageResultDTO.Response)
 * @param {string[]} missing - deduplicated missing markers list
 * @returns {ActionItem[]}
 */
export function buildActionPlan(triage, missing = []) {
  if (!triage) return [];

  const actions = [];

  // P1: Acute alert — always first if present
  if (triage.hasAcuteAlert) {
    actions.push({
      id: 'acute',
      priority: 'P1',
      category: 'Urgent',
      title: 'Immediate clinical review required',
      body: triage.acuteAlertDetail || 'Acute markers detected.',
      window: 'Today',
      tags: ['Urgent'],
    });
  }

  // P1 or P2: Clinical recommendation from Claude
  if (triage.claudeRecommendation) {
    actions.push({
      id: 'rec',
      priority: triage.hasAcuteAlert ? 'P2' : 'P1',
      category: 'Recommendation',
      title: 'Follow clinical recommendation',
      body: triage.claudeRecommendation,
      window: 'This week',
      tags: ['Clinical'],
    });
  }

  // P2: Missing markers
  if (missing.length > 0) {
    actions.push({
      id: 'miss',
      priority: 'P2',
      category: 'Diagnostics',
      title: 'Order missing markers',
      body: `The following markers were absent: ${missing.join(', ')}. A full panel improves scoring accuracy.`,
      window: 'Next visit',
      tags: ['Lab Panel'],
    });
  }

  // P3: Always present — 90-day re-panel
  actions.push({
    id: 'follow',
    priority: 'P3',
    category: 'Monitoring',
    title: 'Schedule 90-day re-panel',
    body: 'Re-test to track response to interventions and compute trajectory across draws.',
    window: '90 days',
    tags: ['Follow-up'],
  });

  // v5.7 rule: cap P1 items at 3
  const p1 = actions.filter(a => a.priority === 'P1').slice(0, 3);
  const rest = actions.filter(a => a.priority !== 'P1');
  return [...p1, ...rest];
}

// ─────────────────────────────────────────────────────────────────────────────
// Serialise / deserialise
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Serialises an action plan array to a JSON string for backend storage.
 * Returns null for empty arrays so the DB column stays clean (not '[]').
 */
export function serializeActionPlan(actionPlan) {
  if (!Array.isArray(actionPlan) || actionPlan.length === 0) return null;
  return JSON.stringify(actionPlan);
}

/**
 * Deserialises stored action plan JSON string back to ActionItem[].
 * Safe for null / malformed input — returns [] on any error.
 */
export function deserializeActionPlan(actionPlanJson) {
  if (!actionPlanJson) return [];
  try {
    const parsed = JSON.parse(actionPlanJson);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * Parses stored checkbox state JSON string.
 * Returns {} on null / malformed input.
 *
 * Format: { "acute": { checked: true, checkedAt: ISO, checkedBy: uuid }, ... }
 */
export function deserializeCheckedState(actionPlanCheckedJson) {
  if (!actionPlanCheckedJson) return {};
  try {
    return JSON.parse(actionPlanCheckedJson);
  } catch {
    return {};
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Checkbox state helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns a new (immutable) checkbox state after toggling one item.
 *
 * @param {object} currentState  - Existing state map
 * @param {string} itemId        - Action item id (e.g. "acute", "rec", "follow")
 * @param {boolean} checked      - New checked value
 * @param {string} navigatorId   - UUID of the currently-logged-in navigator
 */
export function toggleCheckedState(currentState, itemId, checked, navigatorId) {
  return {
    ...currentState,
    [itemId]: {
      checked,
      checkedAt: checked ? new Date().toISOString() : null,
      checkedBy: checked ? (navigatorId || null) : null,
    },
  };
}

/**
 * Groups action items by priority for rendering.
 */
export function groupByPriority(items) {
  return {
    P1: items.filter(i => i.priority === 'P1'),
    P2: items.filter(i => i.priority === 'P2'),
    P3: items.filter(i => i.priority === 'P3'),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// buildConvergence
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// buildDiseaseRisks
// ─────────────────────────────────────────────────────────────────────────────

const _riskLevel = (level) => {
  const s = String(level || '').toLowerCase();
  return s === 'high' ? 'high' : (s === 'moderate' || s === 'medium') ? 'moderate' : s === 'low' ? 'low' : 'unknown';
};
const _statusCls = (s) => s === 'bad' || s === 'critical' ? 'bad' : s === 'warn' || s === 'elevated' ? 'warn' : s === 'good' || s === 'normal' ? 'good' : 'info';
const _parseJson = (raw) => {
  if (!raw) return null;
  try { return JSON.parse(raw.replace(/```json|```/g, '').trim()); } catch { return null; }
};

/** Which Convergence pathway a Disease Risk card speaks to (null when it maps to none of them). */
export function outcomeKeyForRisk(name) {
  const n = String(name || '').toLowerCase();
  if (/cardio|ascvd|heart/.test(n))                      return 'heart';
  if (/diabet|glyc|metabolic/.test(n))                   return 'sugar';
  if (/neuro|dementia|alzheimer|brain/.test(n))          return 'brain';
  if (/cancer|oncolog/.test(n))                          return 'cancer';
  if (/kidney|renal|liver|hepat/.test(n))                return 'organs';
  return null;
}

/**
 * The Disease Risk cards for a triage: the AI's per-disease estimates when the record carries them, otherwise a rule-based
 * estimate from the markers (older records). Shared by the Disease Risk tab, the Convergence tab and the PDF export so all three
 * agree on what the risk is.
 *
 * @returns {{risks: Array, fromClaude: boolean}}
 */
export function buildDiseaseRisks(triage, markers = []) {
  const score = triage?.consensusRiskScore ?? 0;
  const level = _riskLevel(triage?.consensusRiskLevel);
  const claudeResult = _parseJson(triage?.claudeRawResponse);
  const claudeRisks  = claudeResult?.diseaseRisks || claudeResult?.disease_risks || [];
  const fromClaude   = claudeRisks.length >= 1;
  let risks = [];

  if (fromClaude) {
    risks = claudeRisks.map(r => {
      const pct = typeof r.probability === 'number' ? Math.round(r.probability * 100)
                : typeof r.risk        === 'number' ? Math.round(r.risk)
                : typeof r.percentage  === 'number' ? Math.round(r.percentage)
                : 0;
      const band  = pct >= 70 ? 'High' : pct >= 40 ? 'Elevated' : pct >= 20 ? 'Borderline' : 'Low';
      const color = pct >= 70 ? 'high' : pct >= 40 ? 'moderate' : 'low';
      const drivers = (r.markers || r.drivers || r.contributors || [])
        .slice(0, 5)
        .map(m => {
          if (typeof m === 'string') return { name: m, w: 'info' };
          const mObj = markers.find(mk => mk.name?.toLowerCase().includes((m.name || m).toLowerCase()));
          return { name: m.name || m.marker || m, w: mObj ? _statusCls(mObj.status) : 'info' };
        });
      return {
        name: r.disease || r.name || r.category,
        pct, band, color,
        window: r.window || r.timeframe || '10-year estimated',
        baseline: r.baseline || r.populationAvg || 'Population average',
        summary: r.summary || r.description || r.detail || '',
        contributors: drivers,
      };
    }).filter(r => r.name);
    return { risks, fromClaude };
  }

  // Legacy fallback — marker-derived.
  const hasMarker = (name) => markers.some(m => m.name?.toLowerCase().includes(name.toLowerCase()));

  const cvdDrivers = markers.filter(m =>
    ['ldl','hdl','triglyceride','cholesterol','crp','troponin','bnp','apob','homocysteine'].some(k => m.name?.toLowerCase().includes(k))
  );
  if (cvdDrivers.length > 0 || hasMarker('ldl') || hasMarker('crp')) {
    risks.push({
      name: 'Cardiovascular Disease',
      pct: Math.min(Math.round(score * 0.35), 95),
      band: level === 'high' ? 'Elevated' : level === 'moderate' ? 'Borderline' : 'Low',
      color: level,
      window: '10-year ASCVD',
      baseline: '8% population avg',
      summary: triage?.claudeKeyFindings || 'Based on available lipid and inflammatory markers.',
      contributors: cvdDrivers.slice(0,5).map(m => ({ name:`${m.name} ${m.value ?? ''}`, w: _statusCls(m.status) })),
    });
  }

  // Metabolic syndrome is defined by several criteria together (NCEP: three of five). Scoring it from a single marker turned one
  // borderline TG/HDL ratio into "80% Elevated", so it needs at least three metabolic markers, and a borderline one counts half.
  const metDrivers = markers.filter(m =>
    ['glucose','hba1c','insulin','homa','triglyceride','hdl'].some(k => m.name?.toLowerCase().includes(k))
  );
  if (metDrivers.length >= 3) {
    const bad  = metDrivers.filter(m => _statusCls(m.status) === 'bad').length;
    const warn = metDrivers.filter(m => _statusCls(m.status) === 'warn').length;
    const metScore = bad + warn * 0.5;
    const pct = Math.min(Math.round(metScore / metDrivers.length * 80), 90);
    // Band from the percentage shown, so a 16% card can never read "Elevated".
    const metLevel = pct >= 50 ? 'high' : pct >= 25 ? 'moderate' : 'low';
    risks.push({
      name: 'Metabolic Syndrome',
      pct,
      band: metLevel === 'high' ? 'High' : metLevel === 'moderate' ? 'Elevated' : 'Low',
      color: metLevel,
      window: 'Estimated risk',
      baseline: 'NCEP criteria',
      summary: `${bad + warn} of ${metDrivers.length} metabolic markers are outside optimal range.`,
      contributors: metDrivers.slice(0,4).map(m => ({ name:`${m.name} ${m.value ?? ''}`, w: _statusCls(m.status) })),
    });
  }

  const renalDrivers = markers.filter(m => ['egfr','creatinine','urea','kidney','uacr'].some(k => m.name?.toLowerCase().includes(k)));
  if (renalDrivers.length > 0) {
    const rBad = renalDrivers.filter(m => m.status !== 'good').length;
    risks.push({
      name: 'Chronic Kidney Disease',
      pct: Math.min(Math.round(rBad / Math.max(renalDrivers.length,1) * 40), 60),
      band: rBad > 0 ? 'Low–Moderate' : 'Low',
      color: rBad > 0 ? 'moderate' : 'low',
      window: '10-year CKD-3',
      baseline: '7% age-matched',
      summary: `${rBad} renal marker${rBad !== 1 ? 's' : ''} outside optimal range.`,
      contributors: renalDrivers.map(m => ({ name:`${m.name} ${m.value ?? ''}`, w: _statusCls(m.status) })),
    });
  }

  return { risks, fromClaude: false };
}

/**
 * Derives the Convergence tab's model from a marker list: which body systems
 * the markers belong to, which multi-system patterns that implies, which root
 * drivers are active, and which outcome risks those drivers feed.
 *
 * Extracted here because it was computed inline inside ConvergenceTab, which
 * meant the PDF export had no way to reach it — so the export shipped a
 * simplified five-row system list instead, and the exported report said
 * something different from the screen it was exported from. That is the whole
 * class of defect this function exists to prevent: one derivation, two
 * renderers.
 *
 * Pure — no React, no DOM. Same input gives the same model in the tab and in
 * the print HTML.
 *
 * @param {Array<{name:string,value:*,status:string}>} markers
 * @param {Array|null} risks  Disease Risk cards from buildDiseaseRisks; keeps the pathways consistent with that tab
 * @returns {{systems:Array, patterns:Array, drivers:Array, outcomes:Array,
 *            outOfRange:Array, borderline:Array}}
 */
export function buildConvergence(markers = [], risks = null) {
  const SYS = [
    { label: 'Sleep & Recovery',   sub: 'Sleep duration, HRV, Deep sleep, REM',      color: '#2A3E63', icon: '🌙',
      keys: [] },                                     // wearable — no lab markers
    { label: 'Inflammation',       sub: 'hs-CRP, IL-6, TNF-α, Fibrinogen',           color: '#B0432C', icon: '🔥',
      keys: ['crp', 'hs-crp', 'il-6', 'ferritin', 'fibrinogen', 'esr', 'wbc'] },
    { label: 'Glucose Metabolism', sub: 'Fasting glucose, HbA1c, Insulin, HOMA-IR',  color: '#3A7A4C', icon: '💧',
      keys: ['glucose', 'hba1c', 'insulin', 'homa', 'leptin', 'fasting'] },
    { label: 'Cardiovascular',     sub: 'ApoB, hs-CRP, Lp(a), BP, Triglycerides',    color: '#8C2A2F', icon: '❤️',
      keys: ['ldl', 'hdl', 'triglyceride', 'chol', 'apob', 'troponin', 'bnp', 'homocys'] },
    { label: 'Other Systems',      sub: 'Liver enzymes, Vitamin D, Cortisol, Uric acid', color: '#7A7466', icon: '🛡️',
      keys: [] },                                     // catch-all, filled below
  ];

  // First match wins: a marker belongs to one system, so hs-CRP counts once
  // under Inflammation rather than again under Cardiovascular.
  const assigned = new Set();
  const systems = SYS.map(sys => {
    let matched = [];
    if (sys.keys.length > 0) {
      matched = markers.filter(m => {
        if (assigned.has(m.name)) return false;
        const hit = sys.keys.some(k => (m.name || '').toLowerCase().includes(k));
        if (hit) assigned.add(m.name);
        return hit;
      });
    }
    return { ...sys, markers: matched };
  });
  systems[systems.length - 1].markers = markers.filter(m => !assigned.has(m.name));

  const abnormalIn = (label) =>
    !!systems.find(s => s.label === label)?.markers.some(m => m.status !== 'good');

  const patterns = [
    { label: 'Insulin Resistance Cluster',    v: 'HOMA-IR · Insulin · TG/HDL', level: 'danger',
      fromSys: ['Glucose Metabolism', 'Cardiovascular'] },
    { label: 'Chronic Systemic Inflammation', v: 'hs-CRP · IL-6 · GlycA',      level: 'danger',
      fromSys: ['Inflammation', 'Cardiovascular'] },
    { label: 'Atherogenic Dyslipidemia',      v: 'ApoB · TG ↑ · HDL ↓',        level: 'warn',
      fromSys: ['Cardiovascular'] },
    { label: 'Hormonal Dysregulation',        v: 'T3 · T4 · Cortisol',          level: 'warn',
      fromSys: ['Other Systems'] },
  ].filter(p => p.fromSys.some(abnormalIn));

  if (patterns.length === 0) {
    patterns.push({ label: 'No convergent patterns detected', v: '', level: 'info', fromSys: [] });
  }

  const joinMatching = (re, fallback) =>
    markers.filter(m => re.test(m.name || '')).map(m => `${m.name} ${m.value ?? ''}`.trim()).join(' · ') || fallback;

  const drivers = [
    { label: 'CHRONIC\nINFLAMMATION', color: '#B0432C', icon: '🌡️',
      v: joinMatching(/crp|il-6/i, 'hs-CRP, IL-6'),
      active: abnormalIn('Inflammation'),
      detail: 'Persistent low-grade inflammation — ASCVD multiplier and cellular damage driver.',
      feeds: ['Heart & blood vessels', 'Brain health', 'Cancer risk'],
      patterns: ['Chronic Systemic Inflammation', 'Insulin Resistance Cluster'] },
    { label: 'INSULIN\nRESISTANCE', color: '#3A7A4C', icon: '⚙️',
      v: joinMatching(/hba1c|insulin|homa/i, 'HbA1c, HOMA-IR'),
      active: abnormalIn('Glucose Metabolism'),
      detail: 'Hepatic insulin resistance driving glycemic dysregulation and cardiovascular trajectory.',
      feeds: ['Heart & blood vessels', 'Blood sugar & diabetes', 'Liver & kidneys'],
      patterns: ['Insulin Resistance Cluster', 'Atherogenic Dyslipidemia'] },
    { label: 'STRESS &\nRECOVERY DEFICIT', color: '#4A3A8A', icon: '🔋',
      v: 'HRV · Cortisol · Sleep',
      active: false,                                  // wearable-driven; no lab signal yet
      detail: 'HPA axis dysregulation affecting autonomic and metabolic recovery.',
      feeds: ['Brain health', 'Heart & blood vessels'],
      patterns: ['Hormonal Dysregulation'] },
    { label: 'METABOLIC\nOVERLOAD', color: '#B08050', icon: '⚖️',
      v: joinMatching(/triglyceride|ldl|chol/i, 'Triglycerides, LDL'),
      active: abnormalIn('Cardiovascular'),
      detail: 'Excess lipid and metabolic burden driving multi-pathway cardiovascular and hepatic risk.',
      feeds: ['Heart & blood vessels', 'Blood sugar & diabetes'],
      patterns: ['Atherogenic Dyslipidemia', 'Insulin Resistance Cluster'] },
  ];

  // The \n in these labels is LOAD-BEARING: ConvergenceTab renders them with
  // label.split('\n') to lay each out over two lines in the flow diagram.
  // Flattening them silently collapses that layout, so a renderer that wants a
  // single line replaces the newline itself (the print HTML does).
  const OUTCOMES = [
    { key: 'heart',  name: 'Heart & blood vessels',  label: 'HEART &\nBLOOD VESSELS', icon: '♡' },
    { key: 'sugar',  name: 'Blood sugar & diabetes', label: 'BLOOD SUGAR\n& DIABETES', icon: '💧' },
    { key: 'brain',  name: 'Brain health',           label: 'BRAIN\nHEALTH',          icon: '❋' },
    { key: 'cancer', name: 'Cancer risk',            label: 'CANCER\nRISK',           icon: '○' },
    { key: 'organs', name: 'Liver & kidneys',        label: 'LIVER &\nKIDNEYS',       icon: '⬡' },
  ];

  // A pathway is "influenced" when an active root driver feeds it AND the Disease Risk tab does not rate it Low. Without that second
  // test this tab lit up Cardiovascular next to a "Low, 8%" estimate on Disease Risk, and the two tabs read as contradicting each
  // other. When no estimate exists for a pathway (most of them have none), the drivers alone decide.
  const plainName = (d) => String(d.label).replace(/\n/g, ' ').toLowerCase();
  const outcomes = OUTCOMES.map(o => {
    const feeding = drivers.filter(d => d.active && d.feeds.includes(o.name));
    const est = Array.isArray(risks) ? risks.find(r => outcomeKeyForRisk(r.name) === o.key) : null;
    const lowEstimate = !!est && est.pct < 20;
    const active = feeding.length > 0 && !lowEstimate;
    let why;
    if (active) {
      why = `Influenced by ${feeding.map(plainName).join(' and ')}.`
        + (est ? ` Estimated risk: ${est.band.toLowerCase()} (${est.pct}%).` : '');
    } else if (feeding.length > 0) {
      why = `Some related markers are off, but the estimated risk is low (${est.pct}%).`;
    } else {
      why = 'Nothing in this report points here.';
    }
    return { ...o, active, why };
  });

  return {
    systems,
    patterns,
    drivers,
    outcomes,
    outOfRange: markers.filter(m => m.status === 'bad'),
    borderline: markers.filter(m => m.status === 'warn'),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Trajectory projections
// ─────────────────────────────────────────────────────────────────────────────

const HIGHER_IS_BETTER = ['hdl', 'vo2', 'egfr', 'vitamin d', 'testosterone', 'albumin'];

/** True when a higher value of this marker is the healthy direction (HDL, vitamin D…). */
export function isHigherBetter(markerName) {
  return HIGHER_IS_BETTER.some(k => (markerName || '').toLowerCase().includes(k));
}

/**
 * The two 24-month projections a trajectory chart draws: where a marker lands
 * if nothing changes, and where it lands with timely intervention.
 *
 * Lifted out of TrajChart so the PDF can state the same numbers the chart
 * plots. It previously lived inside the chart component, which meant the export
 * had no access to it — and quoting a number in a PDF that disagrees with the
 * chart it came from is worse than omitting it. One implementation, both
 * renderers.
 *
 * The slope cap (±2.5% of the current value per month) is not cosmetic: with
 * two draws days apart, the raw slope extrapolates to physiologically absurd
 * 24-month values, and the chart's y-scale then collapses the real data into a
 * flat line. Keep it.
 *
 * @param {object}  a
 * @param {string}  a.markerName
 * @param {number}  a.last       most recent value
 * @param {number}  a.slope      per-month trend fitted across all draws
 * @param {?number} a.threshold  diagnostic threshold, when one was parsed
 * @param {number}  a.yMin       chart floor — clamps the intervention target
 * @param {number}  a.yMax       chart ceiling — same
 */
export function buildTrajectoryProjection({ markerName, last, slope, threshold, yMin, yMax }) {
  const higherIsBetter = HIGHER_IS_BETTER.some(k => (markerName || '').toLowerCase().includes(k));

  const slopePerMonth = slope ?? 0;
  const cappedSlope = Math.max(
    -Math.abs(last) * 0.025,
    Math.min(Math.abs(last) * 0.025, slopePerMonth),
  );

  const sq24 = +(last + cappedSlope * 24).toFixed(2);
  const sqPts = [
    { x: 0,  y: last },
    { x: 12, y: +(last + cappedSlope * 12).toFixed(2) },
    { x: 24, y: sq24 },
  ];

  // With intervention the marker is steered toward the threshold — just inside
  // it, not exactly onto it, because landing precisely on a diagnostic cut-off
  // is not a clinical goal. Without a threshold, aim 40% of the way toward the
  // better end of the chart.
  const targetY = threshold != null
    ? (higherIsBetter ? Math.min(yMax, threshold * 1.1) : Math.max(yMin, threshold * 0.88))
    : (higherIsBetter ? Math.min(yMax, last + (yMax - last) * 0.4)
                      : Math.max(yMin, last - (last - yMin) * 0.4));
  // "With intervention" must never be a worse outcome than leaving things
  // alone: not worse than where the marker is now, and not worse than the
  // no-action path. Without this, a marker that is already inside its range (or
  // already improving on its own) was shown being steered AWAY from its best
  // value — e.g. LDL 54 "with intervention" ending above "without".
  const clampedTarget = Math.max(yMin, Math.min(yMax, targetY));
  const int24 = +(higherIsBetter
    ? Math.max(clampedTarget, last, sq24)
    : Math.min(clampedTarget, last, sq24)).toFixed(2);
  const intPts = [
    { x: 0,  y: last },
    { x: 12, y: +(last + (int24 - last) * 0.55).toFixed(2) },
    { x: 24, y: int24 },
  ];

  const decimals = yMax < 20 ? 1 : 0;

  return {
    higherIsBetter,
    cappedSlope,
    clampedTarget,
    sq24,
    int24,
    sqPts,
    intPts,
    decimals,
    // Whether each projection is the bad direction, so a renderer colours it
    // without re-deriving the polarity rule.
    sqIsBad:  higherIsBetter ? sq24 < last  : sq24 > last,
    intIsGood: higherIsBetter ? int24 > last : int24 < last,
  };
}

/**
 * The trajectory chart's y-axis: a value -> pixel mapping.
 *
 * A single linear y-axis has to fit BOTH a marker's full historical swing
 * (which can be huge — e.g. TRIG/HDL Ratio dropping from 47 to 4) AND the
 * 24-month projection cluster near its current value (e.g. "without
 * intervention" 2 vs "with intervention" 3) on the same scale. When the
 * projections are close to each other and to `last`, that cluster gets
 * squeezed into a handful of pixels near one edge of the chart and the two
 * projection lines become visually indistinguishable.
 *
 * Fix: a piecewise-linear scale that guarantees the projection cluster at
 * least MIN_FUTURE_FRAC of the plot's pixel height, "borrowing" the rest
 * proportionally from the historical range above/below it. It is continuous
 * by construction (the pieces meet exactly at the cluster's own min/max — no
 * jump, no visual break) and a no-op whenever the cluster already has enough
 * natural room, so a normal, well-scaled chart is pixel-identical to plain
 * linear.
 *
 * Shared between TrajChart (on-screen) and the PDF export's trajectory SVG so
 * both plot every marker on the identical scale — the same reason
 * buildTrajectoryProjection() above is shared rather than reimplemented.
 *
 * @param {object} a
 * @param {number} a.yMin  chart floor (value space)
 * @param {number} a.yMax  chart ceiling (value space)
 * @param {number} a.last  current/most recent value — the projection cluster's anchor
 * @param {number} a.sq24  "without intervention" 24-month projection
 * @param {number} a.int24 "with intervention" 24-month projection
 * @param {?number} a.threshold  diagnostic threshold, when one was parsed
 * @param {number} a.top    pixel Y of the plot's top edge
 * @param {number} a.height plot height in pixels
 * @returns {function(number): number} maps a value to a pixel Y, clamped to [top, top+height]
 */
export function buildYScale({ yMin, yMax, last, sq24, int24, threshold, top, height }) {
  const MIN_FUTURE_FRAC = 0.3;
  const clampToRange = v => Math.max(yMin, Math.min(yMax, v));
  const futureVals = [last, sq24, int24, ...(threshold != null ? [threshold] : [])]
    .map(clampToRange);
  const futMin = Math.min(...futureVals);
  const futMax = Math.max(...futureVals);
  const totalRange = yMax - yMin;
  const naturalFutureFrac = totalRange > 0 ? (futMax - futMin) / totalRange : 1;

  if (naturalFutureFrac >= MIN_FUTURE_FRAC || futMax <= futMin || totalRange <= 0) {
    return v => Math.max(top, Math.min(top + height,
      top + height - ((v - yMin) / (yMax - yMin)) * height
    ));
  }

  const belowSpan = futMin - yMin;
  const aboveSpan = yMax - futMax;
  const outsideSpan = belowSpan + aboveSpan;
  const outsideFrac = 1 - MIN_FUTURE_FRAC;
  const belowFrac = outsideSpan > 0 ? outsideFrac * (belowSpan / outsideSpan) : 0;
  // Breakpoints in value space, ascending, each carrying the fraction of
  // plot height (measured from the bottom) it maps to.
  const stops = [
    { v: yMin,   f: 0 },
    { v: futMin, f: belowFrac },
    { v: futMax, f: belowFrac + MIN_FUTURE_FRAC },
    { v: yMax,   f: 1 },
  ];
  return (val) => {
    const v = clampToRange(val);
    for (let i = 0; i < stops.length - 1; i++) {
      const a = stops[i], b = stops[i + 1];
      if (v <= b.v || i === stops.length - 2) {
        const span = b.v - a.v;
        const t = span > 0 ? (v - a.v) / span : 0;
        const frac = a.f + t * (b.f - a.f);
        return top + height - frac * height;
      }
    }
    return top;
  };
}

/**
 * Whether two marker names plausibly name the same biomarker — used to
 * match a current marker against its own entry in a prior draw, across
 * whatever small wording differences Claude's extraction introduces
 * between two separate calls.
 *
 * 8 Sep 2026: the old heuristic on both sides of this comparison ("does
 * either name contain the OTHER's first word?") treated any shared leading
 * word as a match. "TOTAL TRIIODOTHYRONINE (T3)", "TOTAL THYROXINE (T4)"
 * and "TOTAL IRON BINDING CAPACITY (TIBC)" all start with "total", so T3
 * and T4 could silently borrow TIBC's historical values — a real
 * production case: both showed TIBC's exact prior-draw numbers, and the
 * PDF's "Longitudinal Marker Comparison" table reported a coincidental
 * 0.00 trend for T3 because its real current value happened to equal
 * TIBC's borrowed one. A name match now has to be on the FULL name, or on
 * the marker's own parenthetical code ("(T3)", "(TIBC)") when both names
 * carry one — the code is what actually disambiguates a family of
 * similarly-worded markers; a single shared word never should.
 */
export function matchesMarkerName(a, b) {
  if (!a || !b) return false;
  const norm = (s) => s.toLowerCase().trim().replace(/\s+/g, ' ');
  const na = norm(a), nb = norm(b);
  if (na === nb) return true;

  const codeOf = (s) => {
    const m = s.match(/\(([^)]+)\)\s*$/);
    return m ? m[1].trim() : null;
  };
  const ca = codeOf(na), cb = codeOf(nb);
  if (ca || cb) return ca === cb; // either name carries a code — that's the whole comparison

  // Neither name carries a parenthetical code: fall back to a full-name
  // substring check (handles formatting drift like "IRON" vs "IRON -
  // SERUM") — never truncated to a single word, which is what caused this.
  return na.includes(nb) || nb.includes(na);
}

/**
 * The chart model for one marker: its historical points, the y-scale that fits
 * both history and projections, the parsed diagnostic threshold, and the
 * per-month slope.
 *
 * Extracted from TrajectoryTab for the same reason as the projection above —
 * the PDF needs the identical inputs to report identical numbers.
 *
 * @param {string} markerName
 * @param {Array}  markers            current markers (buildMarkers shape)
 * @param {Array}  history            triage history, NEWEST FIRST
 * @param {Date}   currentDrawDate
 * @param {function} parseHistoryMarkers  (historyEntry) => markers[], injected so
 *        this module needs no knowledge of how a triage row stores its markers
 * @returns {?object} null when the marker has no usable numeric value
 */
export function buildMarkerChartData(markerName, markers, history, currentDrawDate, parseHistoryMarkers) {
  const current = markers.find(m => matchesMarkerName(m.name, markerName));
  if (!current || current.value === undefined || current.value === null) return null;

  const currVal = parseFloat(current.value);
  if (isNaN(currVal)) return null;

  // Fractional months, not calendar-month buckets: two draws in the same month
  // (13 Aug / 21 Aug) must not collapse onto the same x and hide one point.
  const monthDiff = (d1, d2) => (d1 - d2) / (30.4375 * 24 * 3600 * 1000);

  const historicalPts = (history || []).map(h => {
    const hMarkers = parseHistoryMarkers ? parseHistoryMarkers(h) : [];
    const found = hMarkers.find(m => matchesMarkerName(m.name, markerName));
    if (!found) return null;
    const val = parseFloat(found.value);
    if (isNaN(val)) return null;
    return { val, drawDate: h.drawDate ? new Date(h.drawDate) : new Date(h.createdAt) };
  }).filter(Boolean).reverse();                     // history is newest-first

  // A trajectory needs at least two draws; a single reading is reported by
  // trajectoryCoverage() instead of being drawn as a lone dot.
  if (historicalPts.length === 0) return null;

  const allPoints = [...historicalPts.map(p => p.val), currVal];
  const allDates  = [...historicalPts.map(p => p.drawDate), currentDrawDate];
  const xPositions = allDates.map(d => monthDiff(d, currentDrawDate));

  // Slope across ALL draws: a least-squares line through every (date, value),
  // with real dates rather than index spacing. Using only the last two draws let
  // one close-together pair (e.g. 26 Jun and 1 Aug) overrule a longer history.
  // With exactly two draws this is the same as the old two-point slope.
  let slope = 0;
  if (allDates.length > 1) {
    const ts = allDates.map(d => monthDiff(d, allDates[0]));
    const n = ts.length;
    const mt = ts.reduce((a, b) => a + b, 0) / n;
    const mv = allPoints.reduce((a, b) => a + b, 0) / n;
    const sxx = ts.reduce((a, t) => a + (t - mt) ** 2, 0);
    const sxy = ts.reduce((a, t, i) => a + (t - mt) * (allPoints[i] - mv), 0);
    slope = sxx > 0 ? sxy / sxx : 0;
  } else {
    slope = currVal * 0.01;                          // ~1%/month with a single draw
  }

  const threshold = (() => {
    if (!current.range) return null;
    const m = current.range.match(/[\d.]+/);
    return m ? parseFloat(m[0]) : null;
  })();

  // The y-scale has to fit history AND both projections AND the threshold,
  // otherwise the projection lines leave the plot area.
  const cappedEst = Math.max(-Math.abs(currVal) * 0.025,
                             Math.min(Math.abs(currVal) * 0.025, slope));
  const projMax = currVal + cappedEst * 24;
  const projMin = currVal - Math.abs(cappedEst) * 24;

  const allVals = [...allPoints, projMax, projMin, threshold].filter(v => v != null && !isNaN(v));
  const trueMin = Math.min(...allVals);
  const trueMax = Math.max(...allVals);
  const trueRange = trueMax - trueMin || trueMax * 0.2 || 1;
  const pad = Math.max(trueRange * 0.20, currVal * 0.05, 0.3);
  const yMin = +(Math.max(0, trueMin - pad)).toFixed(1);
  const yMax = +(trueMax + pad).toFixed(1);

  const velocity = Math.abs(slope) < 0.001
    ? 'Stable'
    : `${slope > 0 ? '+' : ''}${(slope * 6).toFixed(2)} ${current.unit || ''} / 6mo`;

  return {
    markerName: current.name,
    unit: current.unit || '',
    status: current.status,
    points: allPoints,
    xPositions,
    drawDates: allDates,
    currentDrawDate,
    last: currVal,
    yMin,
    yMax,
    threshold,
    band: current.status === 'bad' ? 'bad' : current.status === 'warn' ? 'warn' : 'good',
    velocity,
    slope,
    draws: allPoints.length,
  };
}

/**
 * Which markers can have a trajectory. A trajectory needs at least two draws
 * with a numeric value; the rest are reported, not charted.
 *   multi       current markers with a value in the current draw and an earlier one
 *   single      current markers whose value appears in this draw only
 *   earlierOnly names with a value in an earlier draw but none in this one
 * `history` is the prior draws (see priorDraws).
 */
export function trajectoryCoverage(markers, history, parseHistoryMarkers) {
  const numeric = (m) => m && m.value !== undefined && m.value !== null && !Number.isNaN(parseFloat(m.value));
  const priorLists = (history || []).map(h => (parseHistoryMarkers ? parseHistoryMarkers(h) : []).filter(numeric));
  const multi = [], single = [];
  (markers || []).forEach(m => {
    if (!numeric(m)) return;
    const inEarlier = priorLists.some(list => list.some(x => matchesMarkerName(x.name, m.name)));
    (inEarlier ? multi : single).push(m);
  });
  const earlierOnly = [];
  priorLists.forEach(list => list.forEach(x => {
    const inCurrent = (markers || []).some(m => numeric(m) && matchesMarkerName(m.name, x.name));
    if (!inCurrent && !earlierOnly.some(n => matchesMarkerName(n, x.name))) earlierOnly.push(x.name);
  }));
  return { multi, single, earlierOnly };
}

/** One sentence for the markers a trajectory cannot be drawn for; '' when there are none. */
export function trajectoryGapNote(coverage, nameFn = (n) => n) {
  const list = (ms) => ms.map(m => nameFn(typeof m === 'string' ? m : m.name)).join(', ');
  const parts = [];
  if (coverage.single.length) parts.push(`Only one reading so far, so no trajectory yet: ${list(coverage.single)}.`);
  if (coverage.earlierOnly.length) parts.push(`Measured in an earlier draw but not in this one: ${list(coverage.earlierOnly)}.`);
  return parts.length ? `A trajectory needs at least 2 draws. ${parts.join(' ')}` : '';
}

/**
 * Earlier draws to compare the current report with. History comes from
 * /triage-history, which can include the report being viewed (or another row
 * for the same draw date, e.g. a re-uploaded file); comparing a result with
 * itself shows "no change" and a duplicate point; it also holds NEWER reports
 * when an older one is opened, which would plot the trajectory backwards
 * (21 Aug "before" 13 Aug). A draw counts as earlier only if it is a different
 * report AND was drawn on an earlier day. Returned newest-first by draw date.
 */
export function priorDraws(history, triage, currentDrawDate) {
  const day = (d) => {
    if (!d) return null;
    if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d)) return d.slice(0, 10);
    const t = new Date(d);
    return Number.isNaN(t.getTime()) ? null : t.toISOString().slice(0, 10);
  };
  const cur = day(currentDrawDate);
  return (Array.isArray(history) ? history : []).filter(h => {
    if (triage?.id && h.id === triage.id) return false;
    const hd = day(h.drawDate || h.createdAt);
    return !(cur && hd && hd >= cur);
  }).sort((a, b) => (day(b.drawDate || b.createdAt) || '').localeCompare(day(a.drawDate || a.createdAt) || ''));
}

// Minimum time between the FIRST and LAST draw before a 24-month prediction is
// shown. The trend is fitted across all draws, so a latest draw close to the one
// before it is fine as long as the history as a whole spans enough time; two draws
// only days apart extrapolate a trend from noise (8 days apart turned uric acid
// 4.8 -> 6.78 into "10.9").
export const MIN_PROJECTION_GAP_MONTHS = 2;
const MONTH_MS = 30.4375 * 24 * 3600 * 1000;

/**
 * Everything the "Where it's heading" block shows for one marker, shared by the
 * on-screen card and the PDF so both quote the same figures and dates.
 *
 *  canProject  false when the first and last draws are < MIN_PROJECTION_GAP_MONTHS apart
 *              (or there is only one draw) — then no predicted values are shown.
 *  planStart / planEnd   the stored intervention date and its 12-month window end;
 *              null when the patient has none.
 *  startOffset / endOffset / todayOffset   months after the latest draw (clamped to
 *              0..24), for placing the plan window and "Today" on the chart's axis;
 *              a plan that began before the latest draw is cut off at the left edge.
 *  sq24 / int24 / decimals / endDate   predicted values and the date they refer to
 *              (latest draw + 24 months).
 *  same        both paths end at the same value (the marker holds or improves
 *              regardless), so one line is shown instead of two figures.
 */
export function buildHeadingInfo({ markerName, drawDates, last, slope, threshold, yMin, yMax, interventionDate, today = new Date() }) {
  const dates = (drawDates || []).map(d => (d instanceof Date ? d : new Date(d)));
  const n = dates.length;
  const lastDraw = n ? dates[n - 1] : null;
  const gapMonths = n > 1 ? (dates[n - 1] - dates[0]) / MONTH_MS : 0;
  const canProject = n > 1 && gapMonths >= MIN_PROJECTION_GAP_MONTHS;

  const iv = interventionDate ? new Date(interventionDate) : null;
  const planStart = iv && !Number.isNaN(iv.getTime()) ? iv : null;
  const planEnd = planStart ? new Date(planStart.getFullYear() + 1, planStart.getMonth(), planStart.getDate()) : null;
  const off = (d) => (lastDraw && d ? Math.max(0, (d - lastDraw) / MONTH_MS) : 0);

  // Forecast horizon: the end of the plan window (plan start + 12 months) when it
  // is still ahead of the latest reading, otherwise 24 months after it.
  const planAhead = planEnd && lastDraw && planEnd - lastDraw >= MONTH_MS;
  const endDate = !lastDraw ? null
    : planAhead ? planEnd
    : new Date(lastDraw.getFullYear(), lastDraw.getMonth() + 24, lastDraw.getDate());
  const horizonMonths = lastDraw ? Math.min(24, off(endDate)) : 0;

  const base = { canProject, gapMonths, planStart, planEnd, endDate, lastDraw, horizonMonths, planAhead: !!planAhead,
    startOffset: Math.min(24, off(planStart)), endOffset: Math.min(24, off(planEnd)), todayOffset: Math.min(24, off(today)) };
  if (!canProject) return base;

  const pr = buildTrajectoryProjection({ markerName, last, slope, threshold, yMin, yMax });
  // Values at the horizon h (months): no action continues the capped trend; with
  // action moves toward the target along the same curve the 24-month path uses
  // (55% of the way by month 12, all of it by 24), and is never worse than
  // standing still or than no action.
  const h = horizonMonths;
  const frac = h <= 12 ? 0.55 * (h / 12) : 0.55 + 0.45 * ((h - 12) / 12);
  const sqH = +(last + pr.cappedSlope * h).toFixed(2);
  const rawInt = last + (pr.clampedTarget - last) * frac;
  const intH = +(pr.higherIsBetter ? Math.max(rawInt, last, sqH) : Math.min(rawInt, last, sqH)).toFixed(2);
  return {
    ...base, ...pr, sqH, intH,
    sqIsBad: pr.higherIsBetter ? sqH < last : sqH > last,
    intIsGood: pr.higherIsBetter ? intH > last : intH < last,
    same: sqH === intH,
  };
}

/**
 * Who approved the lifestyle points a patient sees: the distinct doctor names in the order
 * they appear, and the most recent approval date. Empty names = the server did not say.
 */
export function lifestyleReviewers(items) {
  const names = [];
  let latest = null;
  (items || []).forEach((i) => {
    if (i?.reviewedByName && !names.includes(i.reviewedByName)) names.push(i.reviewedByName);
    if (i?.reviewedAt && (!latest || new Date(i.reviewedAt) > new Date(latest))) latest = i.reviewedAt;
  });
  return { names, latest, label: names.join(' and ') };
}
